import { useEffect, useMemo, type RefObject } from 'react';

export type HotkeyOS = 'mac' | 'windows' | 'linux' | 'unknown';

export const detectOS = (): HotkeyOS => {
  if (typeof window === 'undefined') return 'unknown';
  const ua = (navigator.userAgent || '').toLowerCase();
  const plat = (navigator.platform || '').toLowerCase();
  if (plat.includes('mac') || ua.includes('mac os')) return 'mac';
  if (plat.includes('win')) return 'windows';
  if (plat.includes('linux')) return 'linux';
  return 'unknown';
};

export const isEditableTarget = (el: EventTarget | null) => {
  if (!el || !(el as HTMLElement).closest) return false;
  const node = (el as HTMLElement).closest('*') as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  const editable =
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    (node as HTMLElement).isContentEditable ||
    node.getAttribute('role') === 'textbox';
  return editable;
};

type Combo = string; // es: "Primary+K", "Cmd+Alt+ArrowUp", "Ctrl+Shift+P", "Escape"

type Hotkey = {
  combo: Combo;
  handler: (ev: KeyboardEvent) => void;
  /** di default true */
  preventDefault?: boolean;
  /** di default false */
  stopPropagation?: boolean;
  /** se true, una sola volta poi si deregistra */
  once?: boolean;
  /** override per trigger in input */
  allowInInputs?: boolean;
  /** abilita/disabilita a runtime */
  enabled?: boolean;
};

type Options = {
  /** listener su window (default) o su un elemento specifico */
  targetRef?: RefObject<HTMLElement>;
};

type ParsedCombo = {
  key: string; // normalizzato: 'k', 'escape', 'arrowup', ...
  ctrl: boolean;
  meta: boolean; // ⌘ su mac
  shift: boolean;
  alt: boolean; // Alt / Option
};

const specialKeyAliases: Record<string, string> = {
  esc: 'escape',
  return: 'enter',
  space: ' ',
  spacebar: ' ',
  plus: '+',
  up: 'arrowup',
  down: 'arrowdown',
  left: 'arrowleft',
  right: 'arrowright',
  pagedown: 'pagedown',
  pageup: 'pageup',
  delete: 'delete',
  backspace: 'backspace',
  tab: 'tab',
};

export const normalizeKey = (k: string) => {
  const low = k.toLowerCase();
  if (specialKeyAliases[low] !== undefined) return specialKeyAliases[low];
  return low.length === 1 ? low : low; // per tasti alfabetici/simboli
};

export const parseCombo = (combo: Combo, os: HotkeyOS): ParsedCombo => {
  const parts = combo
    .split('+')
    .map((s) => s.trim())
    .filter(Boolean);
  let ctrl = false,
    meta = false,
    shift = false,
    alt = false;
  let key = '';

  for (const raw of parts) {
    const p = raw.toLowerCase();
    if (p === 'primary') {
      if (os === 'mac') meta = true;
      else ctrl = true;
    } else if (p === 'cmd' || p === 'meta' || p === '⌘') {
      meta = true;
    } else if (p === 'ctrl' || p === 'control') {
      ctrl = true;
    } else if (p === 'shift' || p === '⇧') {
      shift = true;
    } else if (p === 'alt' || p === 'option' || p === '⌥') {
      alt = true;
    } else {
      key = normalizeKey(p);
    }
  }

  return { key, ctrl, meta, shift, alt };
};

export const matchEvent = (ev: KeyboardEvent, pc: ParsedCombo) => {
  const key = normalizeKey(ev.key);
  // Nota: su Mac, molti usano metaKey; su Win il "Cmd" non esiste.
  return (
    !!ev.ctrlKey === pc.ctrl &&
    !!ev.metaKey === pc.meta &&
    !!ev.shiftKey === pc.shift &&
    !!ev.altKey === pc.alt &&
    // tasto
    (pc.key === '' ? true : key === pc.key)
  );
};

/** Builds a normalized combo string from a keydown event (e.g. `Primary+k`, `ctrl+n`). */
export const eventToCombo = (ev: KeyboardEvent, os: HotkeyOS): string => {
  const modifierKeys = new Set(['control', 'shift', 'alt', 'meta']);
  const key = normalizeKey(ev.key);
  if (modifierKeys.has(key)) return '';

  const parts: string[] = [];
  if (ev.ctrlKey) parts.push('ctrl');
  if (ev.metaKey) parts.push(os === 'mac' ? 'primary' : 'meta');
  if (ev.shiftKey) parts.push('shift');
  if (ev.altKey) parts.push('alt');
  parts.push(key);
  return parts.join('+');
};

export function useHotkeys(hotkeys: Hotkey[], options?: Options) {
  const os = useMemo(detectOS, []);
  const parsed = useMemo(
    () =>
      hotkeys.map((h) => ({
        ...h,
        parsed: parseCombo(h.combo, os),
        preventDefault: h.preventDefault ?? true,
        stopPropagation: h.stopPropagation ?? false,
        allowInInputs: h.allowInInputs ?? false,
        enabled: h.enabled ?? true,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(hotkeys), os],
  );

  useEffect(() => {
    const target: any =
      options?.targetRef?.current ?? (typeof window !== 'undefined' ? window : null);
    if (!target) return;

    // per gestire "once"
    const onceSet = new Set<number>();

    const onKeyDown = (ev: KeyboardEvent) => {
      for (let i = 0; i < parsed.length; i++) {
        const hk = parsed[i];
        if (!hk?.enabled) continue;

        // evita input/textarea a meno che non permesso
        if (!hk?.allowInInputs && isEditableTarget(ev.target)) continue;

        // evita key repeat
        if (ev.repeat) continue;

        if (matchEvent(ev, hk?.parsed)) {
          if (hk?.preventDefault) ev.preventDefault();
          if (hk?.stopPropagation) ev.stopPropagation();

          // esegui
          hk?.handler(ev);

          // se once: mark e disattiva
          if (hk?.once) {
            onceSet.add(i);
          }
          break; // evita di far scattare più handler sulla stessa pressione
        }
      }

      if (onceSet.size) {
        // disattiva gli 'once'
        onceSet.forEach((idx) => {
          (parsed[idx] as any).enabled = false;
        });
        onceSet.clear();
      }
    };

    target.addEventListener('keydown', onKeyDown, { capture: false });
    return () => {
      target.removeEventListener('keydown', onKeyDown, { capture: false } as any);
    };
  }, [parsed, options?.targetRef]);
}
